import { stepLabel } from "../stepLabel.js";
import { useEffect, useState } from "react";
import { fetchBreakdown, send, type Board, type WorkflowStateRow } from "../api.js";

/**
 * The engineer's own screen.
 *
 * The default landing for an Engineer, because Today is scheduler-shaped — it
 * leads with what needs a decision across the whole shop, which is not this
 * reader's question. Theirs is "what am I on, and what do I owe".
 *
 * Only the fields deny() calls `phase.progress` are editable by an engineer here:
 * state, actual hours and the description. Dates, dependencies, who a step
 * belongs to and the estimate are the plan, and belong to a manager or an
 * admin — so an engineer sees them, and not as inputs.
 */

export interface MyPhase {
  phaseId: string;
  projectId: string;
  phaseCode: string | null;
  description: string | null;
  reqDueDate: string | null;
  estHours: number;
  actHours: number;
  workflowState: string;
  /** At or past its estimate and still open. Derived on the server. */
  overEstimate: boolean;
  /** The day it was completed, when it has been. */
  completedOn: string | null;
}

/**
 * The phases assigned to whoever is looking.
 *
 * An unlinked account owns nothing. Guarding that explicitly matters: a null
 * resourceId compared against an unassigned assignment's null resourceId would
 * otherwise hand a stranger the whole pool.
 */
export function myPhases(board: Board, asResourceId?: string | null): MyPhase[] {
  const me = asResourceId ?? board.user.resourceId;
  if (!me) return [];
  const mine = new Set(board.assignments.filter((a) => a.resourceId === me).map((a) => a.phaseId));
  return board.phases
    .filter((p) => mine.has(p.phaseId))
    .map((p) => ({
      phaseId: p.phaseId,
      projectId: p.projectId,
      phaseCode: p.phaseCode ?? null,
      description: p.description ?? null,
      reqDueDate: p.reqDueDate ?? null,
      estHours: p.estHours,
      actHours: p.actHours,
      workflowState: p.workflowState,
      overEstimate: p.overEstimate === true,
      completedOn: p.completedOn ?? null,
    }));
}

/** A due date to sort on: the dated steps first, earliest first, and the undated last. */
const dueKey = (d: string | null): string => d ?? "9999-12-31";

/**
 * The steps as one list, soonest due first, the undated last.
 *
 * Ties fall back to the project and then the step, so the order never jitters
 * when two steps share a date.
 */
export function sortByDue(phases: MyPhase[]): MyPhase[] {
  return [...phases].sort(
    (a, b) =>
      dueKey(a.reqDueDate).localeCompare(dueKey(b.reqDueDate))
      || a.projectId.localeCompare(b.projectId)
      || (a.phaseCode ?? "").localeCompare(b.phaseCode ?? ""),
  );
}

/**
 * Whose work a manager or admin may look at from here: an admin everyone on the
 * board, a manager the people on the teams they manage, each with themselves
 * first when they have a place on the board. Pool placeholders and inactive
 * people have no work of their own to review, so they are left out.
 *
 * This only decides who is offered. What a viewer may change in the list that
 * opens is still decided by the server, as it is for any edit.
 */
export function viewablePeople(board: Board): { resourceId: string; name: string }[] {
  const { role, resourceId: me, managedTeamIds } = board.user;
  if (role !== "admin" && role !== "manager") return [];
  const managed = new Set(managedTeamIds ?? []);
  return board.resources
    .filter((r) => r.active && !r.isPool && r.resourceId !== me)
    .filter((r) => role === "admin" || (r.teamId !== null && managed.has(r.teamId)))
    .map((r) => ({ resourceId: r.resourceId, name: r.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Whether a phase is actually late — past its due date and still open.
 *
 * "Owed less than promised" (estHours - actHours > 0) is a different question
 * from "still open", and answering the second with the first breaks the
 * moment someone logs more hours than the estimate: a step can run well past
 * its estimate and still be genuinely late, sitting in a non-terminal state
 * with a due date behind it. `isTerminal` is what workflow state itself
 * considers done, so it is the one test used everywhere "late" is shown —
 * the KPI above the table and the red highlighting in it can then never
 * disagree about the same phase.
 */
export function isLatePhase(p: MyPhase, today: string, terminal: ReadonlySet<string>): boolean {
  return !!p.reqDueDate && p.reqDueDate < today && !terminal.has(p.workflowState);
}

export function MyWork({ board, reload, onOpenProject }: {
  board: Board; reload: () => void;
  /** Opens a project, with Back returning here. */
  onOpenProject?: (projectId: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The board doesn't carry the workflow-state list (only /breakdown does —
  // there is no standalone GET for it), so it's fetched here rather than
  // invented as a Board field. /breakdown has no role gate, so an engineer
  // can read it same as anyone signed in.
  const [states, setStates] = useState<WorkflowStateRow[]>([]);
  // Which field of which phase the server most recently refused — a
  // row-level tell alongside the page-level banner, since an engineer is the
  // least likely reader to know what a 403 means. Cleared at the start of
  // the next attempt on any field, same as `err`.
  const [failedField, setFailedField] = useState<{ phaseId: string; field: string } | null>(null);
  // The hours and description inputs are uncontrolled (defaultValue, not
  // value) so typing doesn't fight the caret on every keystroke. That means
  // a refusal doesn't put the rejected text back on its own — nothing else
  // touches the DOM node. Bumping this per phaseId+field on failure changes
  // the input's key, which forces React to remount it and re-read
  // defaultValue from `p`, the value the server actually holds, since a
  // refusal never changed the board.
  const [resetTick, setResetTick] = useState<Record<string, number>>({});
  // A manager or admin can look at a team member's list. Null is their own.
  const [who, setWho] = useState<string | null>(null);
  // Completed steps leave the list; this brings them back, so one marked
  // complete by mistake can still be reached and reopened.
  const [showDone, setShowDone] = useState(false);
  const people = viewablePeople(board);
  // A choice that is no longer on offer (a different view, a changed board) falls back to their own.
  const viewing = who && people.some((x) => x.resourceId === who) ? who : null;
  const subject = viewing ?? board.user.resourceId;
  const subjectName = viewing ? people.find((x) => x.resourceId === viewing)?.name ?? "" : null;
  const picker = people.length > 0 && (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
      <label htmlFor="mywork-person" style={{ fontSize: 13 }}>Showing</label>
      <select id="mywork-person" value={viewing ?? ""} onChange={(e) => setWho(e.target.value || null)}>
        <option value="">{board.user.resourceId ? "my work" : "choose a person…"}</option>
        {people.map((x) => <option key={x.resourceId} value={x.resourceId}>{x.name}</option>)}
      </select>
      {viewing && (
        <span className="muted" style={{ fontSize: 12 }}>
          You are looking at {subjectName}'s list. Changes you make are saved as you, under your own permissions.
        </span>
      )}
    </div>
  );

  useEffect(() => {
    let live = true;
    fetchBreakdown()
      .then((d) => { if (live) setStates(d.workflowStates); })
      .catch(() => { /* the state select just falls back to the raw value below */ });
    return () => { live = false; };
  }, []);

  if (!subject) {
    return (
      <div>
      {picker}
      <div className="run-sec">
        <h3>Not linked yet</h3>
        <p>
          Your account is not linked to anyone on the board, so it has no work of its own.
          An admin can link it by putting your email address against your name on the
          People screen. Until then you can see the whole schedule, but none of it is yours.
        </p>
      </div>
      </div>
    );
  }

  const all = myPhases(board, subject);
  // The estimate is the plan: a manager or an admin re-estimates, an engineer asks.
  const canEstimate = board.user.role !== "engineer";
  const terminal = new Set(states.filter((s) => s.isTerminal).map((s) => s.stateId));
  // The figures above the list, and the list itself, are open work. A completed
  // step has nothing left to owe, whatever its estimate less actual says.
  const mine = all.filter((p) => !terminal.has(p.workflowState));
  const doneCount = all.length - mine.length;
  const rows = sortByDue(showDone ? all : mine);

  async function patch(phaseId: string, body: Record<string, unknown>) {
    const field = Object.keys(body)[0]!;
    setBusy(phaseId); setErr(null); setFailedField(null);
    try {
      await send(`/phases/${phaseId}`, "PATCH", body);
      reload();
    } catch (e) {
      setErr((e as Error).message);
      setFailedField({ phaseId, field });
      // Force the uncontrolled input to remount on the next render, so it
      // drops the rejected value and shows what the board actually holds.
      const key = `${phaseId}:${field}`;
      setResetTick((t) => ({ ...t, [key]: (t[key] ?? 0) + 1 }));
    } finally {
      setBusy(null);
    }
  }

  const remaining = mine.reduce((n, p) => n + Math.max(p.estHours - p.actHours, 0), 0);
  const isLate = (p: MyPhase) => isLatePhase(p, board.today, terminal);
  const late = mine.filter(isLate).length;

  return (
    <div>
      {picker}
      <div className="kpis">
        <Kpi label="Steps" value={mine.length} sub={viewing ? `assigned to ${subjectName}` : "assigned to you"} />
        <Kpi label="Remaining" value={Math.round(remaining * 10) / 10} sub="estimate less actual" />
        <Kpi label="Past due" value={late} sub="promised before today" crit={late > 0} />
      </div>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      <p className="hint" style={{ marginTop: 0 }}>
        State, hours worked and the description are yours to change. Dates, dependencies, who a
        step belongs to and the estimate are the plan — ask your manager. A step that has used
        up its estimate is flagged <b>over estimate</b>: ask your manager to re-estimate it, or
        complete it. A new estimate changes the schedule at the next solve rather than immediately.
      </p>

      {doneCount > 0 && (
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13, marginBottom: 8 }}>
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          Show completed ({doneCount})
        </label>
      )}

      {!rows.length && (
        <p className="muted">
          {doneCount > 0
            ? "No open steps. Tick Show completed to see the finished ones."
            : viewing ? `Nothing is assigned to ${subjectName} right now.` : "Nothing is assigned to you right now."}
        </p>
      )}

      {rows.length > 0 && (
        <div className="scroll">
          <table className="qt" style={{ minWidth: 1180, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 90 }} /><col style={{ width: 200 }} /><col style={{ width: 120 }} />
              <col style={{ width: 260 }} /><col style={{ width: 110 }} /><col style={{ width: 80 }} />
              <col style={{ width: 80 }} /><col style={{ width: 240 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Project</th><th>Project name</th><th>Step</th><th>Description</th><th>Due</th>
                <th className="r">Est</th><th className="r">Act</th><th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                // A red outline on whichever field the server just refused
                // — the row's own tell, alongside the banner above.
                const badField = (field: string) =>
                  failedField?.phaseId === p.phaseId && failedField.field === field
                    ? { outline: "2px solid var(--crit)" }
                    : undefined;
                const tick = (field: string) => resetTick[`${p.phaseId}:${field}`] ?? 0;
                const projectName = board.projects.find((x) => x.projectId === p.projectId)?.description;
                return (
                  <tr key={p.phaseId} style={{ opacity: busy === p.phaseId ? 0.5 : 1 }}>
                    <td className="mono">
                      {onOpenProject
                        ? <a href="#projects" title="Open this project" onClick={(e) => { e.preventDefault(); onOpenProject(p.projectId); }}>{p.projectId}</a>
                        : p.projectId}
                    </td>
                    <td title={projectName ?? undefined} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {projectName ?? "—"}
                    </td>
                    <td className="mono"><b>{p.phaseCode ?? stepLabel(p, p.phaseId)}</b></td>
                    <td>
                      <input
                        key={`description-${tick("description")}`}
                        className="num" style={{ width: "100%", ...badField("description") }}
                        defaultValue={p.description ?? ""}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (p.description ?? "")) void patch(p.phaseId, { description: v || null });
                        }}
                      />
                    </td>
                    <td className={`mono ${isLate(p) ? "late" : ""}`}>
                      {p.reqDueDate ?? "—"}
                    </td>
                    <td className="r">
                      {canEstimate ? (
                        <input
                          key={`estHours-${tick("estHours")}`}
                          className="num" type="number" step="0.5" min="0" defaultValue={p.estHours}
                          style={badField("estHours")}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isFinite(v) && v !== p.estHours) void patch(p.phaseId, { estHours: v });
                          }}
                        />
                      ) : (
                        <span className="mono">{p.estHours}</span>
                      )}
                    </td>
                    <td className="r">
                      <input
                        key={`actHours-${tick("actHours")}`}
                        className="num" type="number" step="0.5" min="0" defaultValue={p.actHours}
                        style={badField("actHours")}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v) && v !== p.actHours) void patch(p.phaseId, { actHours: v });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        value={p.workflowState}
                        style={badField("workflowState")}
                        onChange={(e) => void patch(p.phaseId, { workflowState: e.target.value })}
                      >
                        {states.map((s) => (
                          <option key={s.stateId} value={s.stateId}>{s.label ?? s.stateId}</option>
                        ))}
                        {!states.some((s) => s.stateId === p.workflowState) && (
                          <option value={p.workflowState}>{p.workflowState}</option>
                        )}
                      </select>
                      {terminal.has(p.workflowState) && p.completedOn && (
                        <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>done {p.completedOn}</span>
                      )}
                      {p.overEstimate && (
                        <span
                          className="chip exp" style={{ marginLeft: 8 }}
                          title={canEstimate
                            ? "At or past its estimate and still open. Re-estimate it, or complete it."
                            : "At or past its estimate and still open. Ask your manager to re-estimate it, or complete it if the work is done."}
                        >
                          over estimate
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, crit }: { label: string; value: number; sub: string; crit?: boolean }) {
  return (
    <div className="kpi">
      <span className="lab">{label}</span>
      <span className={`val${crit ? " crit" : ""}`}>{value}</span>
      <span className="sub">{sub}</span>
    </div>
  );
}
